import { memo, useCallback } from 'react';

import CustomSelect from 'components/custom-select';
import IndicadoresDaFila, { TomDoIndicador } from 'pages/RealizacaoDestaque/components/IndicadoresDaFila';
import { FOCO_VISIVEL } from 'utils/foco';

import { CriterioDaFila, IndicadorDaFilaEcoa, OPCOES_DE_ORDEM_ECOA, OrdemDaFila } from '../fila-ecoa';

export interface IndicadoresEFiltrosDaFilaEcoaProps {
  indicadores: IndicadorDaFilaEcoa[];
  ativo: CriterioDaFila | null;
  /** A página alterna o filtro: acionar o indicador ativo remove o filtro (ECN-50). */
  onIndicador: (criterio: CriterioDaFila) => void;
  busca: string;
  onBusca: (texto: string) => void;
  ordem: OrdemDaFila;
  onOrdem: (ordem: OrdemDaFila) => void;
  /** Ações na lista depois dos filtros e no escopo da fase. */
  naLista: number;
  noEscopo: number;
  onLimparFiltros: () => void;
}

export const ID_DA_BUSCA_DA_FILA_ECOA = 'fila-ecoa-busca';

/** "Limpar filtros" some depois do clique: o foco vai para a busca (ECN-65, WCAG 2.4.3). */
export function limparEFocarNaBuscaDaFilaEcoa(onLimparFiltros: () => void) {
  onLimparFiltros();
  document.getElementById(ID_DA_BUSCA_DA_FILA_ECOA)?.focus();
}

const TOM: Record<CriterioDaFila, TomDoIndicador> = {
  aguardando: 'rosa',
  cota: 'verde',
  ciencia: 'ciano',
  'sem-feedback': 'azul',
};

const ROTULO = 'mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft';
const CAMPO = `w-full rounded-jj-field border border-content-soft bg-brand-white px-3.5 py-[11px] text-[13px] text-content ${FOCO_VISIVEL}`;

/** Os blocos da fila re-renderizam a cada leitura; o `react-select` só re-renderiza quando as props dele mudam. */
const CampoDeSelecao = memo(CustomSelect);

/** Indicadores que filtram, "Buscar na lista", "Ordenar por", chips dos filtros ativos e o anúncio da contagem. */
function IndicadoresEFiltrosDaFilaEcoa(props: IndicadoresEFiltrosDaFilaEcoaProps) {
  const { indicadores, ativo, busca, onOrdem } = props;
  const aoOrdenar = useCallback((opcao: any) => opcao?.value && onOrdem(opcao.value), [onOrdem]);
  const termo = busca.trim();
  const indicadorAtivo = indicadores.find(indicador => indicador.criterio === ativo);

  const chips = [
    ...(indicadorAtivo ? [{ rotulo: indicadorAtivo.rotulo, remover: () => props.onIndicador(indicadorAtivo.criterio) }] : []),
    ...(termo ? [{ rotulo: `Busca: ${termo}`, remover: () => props.onBusca('') }] : []),
  ];

  return (
    <div>
      <IndicadoresDaFila
        indicadores={indicadores.map(indicador => ({
          chave: indicador.criterio,
          rotulo: indicador.rotulo,
          valor: indicador.valor,
          tom: TOM[indicador.criterio],
          ativo: indicador.criterio === ativo,
          onFiltrar: () => props.onIndicador(indicador.criterio),
        }))}
      />

      <div className="mt-4 flex flex-wrap items-end gap-2.5 border-t border-surface-200 pt-4">
        <div className="min-w-[200px] flex-[1_1_300px]">
          <label htmlFor={ID_DA_BUSCA_DA_FILA_ECOA} className={ROTULO}>
            Buscar na lista
          </label>
          <input
            id={ID_DA_BUSCA_DA_FILA_ECOA}
            type="search"
            value={busca}
            onChange={evento => props.onBusca(evento.target.value)}
            placeholder="Nome da ação, integrante, chave ou eixo"
            className={CAMPO}
          />
        </div>
        <div className="min-w-[200px] flex-[0_1_230px]">
          <label htmlFor="fila-ecoa-ordem" className={ROTULO}>
            Ordenar por
          </label>
          <CampoDeSelecao
            inputId="fila-ecoa-ordem"
            ariaLabel="Ordenar por"
            variant="light"
            options={OPCOES_DE_ORDEM_ECOA}
            value={OPCOES_DE_ORDEM_ECOA.find(opcao => opcao.value === props.ordem)}
            onChange={aoOrdenar}
          />
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span id="filtros-ativos-da-fila-ecoa" className="text-[11px] text-content-soft">
            Filtros ativos
          </span>
          <div role="group" aria-labelledby="filtros-ativos-da-fila-ecoa" className="flex flex-wrap gap-2">
            {chips.map(chip => (
              // SPEC_DEVIATION: texto `brand-blue-dark` no lugar do `brand-blue` do design. Reason: ECN-61, o azul sobre
              // `surface-100` fica em 4,35:1 (src/test-utils/contraste.ts).
              <button
                key={chip.rotulo}
                type="button"
                aria-label={`Remover filtro ${chip.rotulo}`}
                onClick={chip.remover}
                className={`inline-flex cursor-pointer items-center gap-[7px] rounded-jj-pill border border-brand-blue bg-surface-100 px-[11px] py-[5px] text-[11px] font-bold text-brand-blue-dark ${FOCO_VISIVEL}`}
              >
                {chip.rotulo}
                <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => limparEFocarNaBuscaDaFilaEcoa(props.onLimparFiltros)}
            className={`ml-auto cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.05em] text-brand-blue ${FOCO_VISIVEL}`}
          >
            Limpar filtros
          </button>
        </div>
      )}

      {/* Sempre montada: anuncia a contagem quando a busca ou um indicador a mudam (ECN-64, WCAG 4.1.3). */}
      <p role="status" className="sr-only">
        {`${props.naLista} de ${props.noEscopo} ações na lista`}
      </p>
    </div>
  );
}

/** Memoizado: a página da fila re-renderiza a cada leitura e a cada segundo, e o `react-select` é caro. */
export default memo(IndicadoresEFiltrosDaFilaEcoa);
